import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Box, Typography, Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import {
  ThemeScope, Button, Input, Label,
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, Separator,
} from "./ui/shadcn";

// Design tokens (Design 08 - light modern ERP)
const HERO_GRADIENT = "linear-gradient(180deg, #f8fafc 0%, #eef2f8 100%)";
const BANNER_GRADIENT = "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)";
const BTN_GRADIENT = "linear-gradient(180deg, #2f6df0 0%, #1d4ed8 100%)";
const INK = "#0f172a";
const MUTED = "#64748b";
const EASE = [0.22, 1, 0.36, 1];

// shadcn tokens for this design
const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": INK,
  "--card": "#ffffff",
  "--card-foreground": INK,
  "--primary": "#1d4ed8",
  "--primary-foreground": "#ffffff",
  "--secondary": "#eff6ff",
  "--secondary-foreground": "#1e3a8a",
  "--muted": "#f8fafc",
  "--muted-foreground": MUTED,
  "--accent": "#eff6ff",
  "--destructive": "#be123c",
  "--destructive-foreground": "#ffffff",
  "--border": "#e2e8f0",
  "--input": "#e2e8f0",
  "--ring": "#2563eb",
  "--radius": "0.75rem",
};

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

function LoginDesign08({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Visual helpers (styling / motion only) ─────────────────────────────────
  const dist = reduce ? 0 : 18;

  // Staggered entrance for a group of children
  const groupVariants = {
    hidden: { opacity: 1 },
    show: { opacity: 1, transition: { staggerChildren: reduce ? 0 : 0.07, delayChildren: reduce ? 0 : 0.12, duration: 0.3 } },
    exit: { opacity: 0, y: reduce ? 0 : -10, transition: { duration: 0.18 } },
  };
  const itemVariants = {
    hidden: { opacity: 1, y: dist },
    show: { opacity: 1, y: 0, transition: { duration: reduce ? 0.2 : 0.45, ease: EASE } },
  };
  // Inputs/buttons never fade from opacity 0, so they can't get stuck invisible
  const fieldVariants = {
    hidden: { y: dist },
    show: { y: 0, transition: { duration: reduce ? 0.2 : 0.45, ease: EASE } },
  };

  // Keyboard support for the company radio cards (arrow keys move selection)
  const handleCompanyKeyDown = (e, idx) => {
    const key = e.key;
    if (key === " " || key === "Enter") {
      e.preventDefault();
      setSelectedCompany(companies[idx]);
      return;
    }
    let next = null;
    if (key === "ArrowDown" || key === "ArrowRight") next = (idx + 1) % companies.length;
    if (key === "ArrowUp" || key === "ArrowLeft") next = (idx - 1 + companies.length) % companies.length;
    if (next === null) return;
    e.preventDefault();
    setSelectedCompany(companies[next]);
    const el = document.getElementById("ld8-company-" + next);
    if (el) el.focus();
  };

  const modules = ["Accounting", "GST Billing", "Inventory", "Payables"];

  const capsHelper = (
    <AnimatePresence initial={false}>
      {capsLockOn && (
        <motion.div
          key="caps"
          role="status"
          initial={{ opacity: 1, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: reduce ? 0.1 : 0.2 }}
          style={{ overflow: "hidden" }}
        >
          <span className="ld8-caps"><WarningAmberIcon sx={{ fontSize: 14, color: "#f59e0b" }} />Caps Lock is ON</span>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="err"
          initial={{ opacity: 1, y: reduce ? 0 : -8 }}
          animate={{ opacity: 1, y: 0, x: reduce ? 0 : [0, -7, 7, -4, 4, 0] }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: reduce ? 0.15 : 0.45 }}
          style={{ overflow: "hidden" }}
        >
          <Alert variant="destructive" className="ld8-alert">
            <WarningAmberIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const renderFooter = (sx) => (
    <Typography
      component={motion.p}
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      transition={{ delay: reduce ? 0 : 0.9, duration: 0.6 }}
      align="center"
      sx={{ fontSize: "0.75rem", color: MUTED, m: 0, ...sx }}
    >
      <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
      {new Date().getFullYear()} Powered by{" "}
      Soltrack Technologies Pvt Ltd
    </Typography>
  );

  return (
    <ThemeScope
      vars={THEME_VARS}
      className="login08-root ld8-page"
      style={{
        position: "relative",
        width: "100%",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        background: HERO_GRADIENT,
      }}
    >
      <style>{[
        "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
        ".login08-root, .login08-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
        ".ld8-page { min-height: 100vh; }",
        "@supports (min-height: 100dvh) { .ld8-page { min-height: 100dvh; } }",

        /* Card */
        ".login08-root .ld8-card { border: 1px solid #e2e8f0; border-radius: 18px; background: #fff; box-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 24px 48px -20px rgba(15,23,42,0.18); position: relative; z-index: 1; overflow: hidden; }",
        ".login08-root .ld8-head { flex-direction: row; align-items: center; gap: 1rem; padding: 2rem 2rem 1.4rem; }",
        ".login08-root .ld8-headtext { min-width: 0; display: flex; flex-direction: column; gap: .3rem; }",
        ".login08-root .ld8-badge { width: 50px; height: 50px; border-radius: 15px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #fff; background: " + BTN_GRADIENT + "; box-shadow: 0 10px 22px -6px rgba(37,99,235,0.55); }",
        ".login08-root .ld8-title { font-size: 1.7rem; font-weight: 800; letter-spacing: -.5px; line-height: 1.15; color: var(--foreground); }",
        ".login08-root .ld8-desc { font-size: .86rem; line-height: 1.45; color: var(--muted-foreground); }",
        ".login08-root .ld8-card .ui-card-content { padding: 0 2rem 1.25rem; }",
        ".login08-root .ld8-card .ui-card-footer { padding: 0 2rem 1.9rem; justify-content: center; text-align: center; font-size: .875rem; color: var(--muted-foreground); }",
        "@media (max-width: 599.95px) { .login08-root .ld8-head { padding: 1.5rem 1.4rem 1.1rem; } .login08-root .ld8-card .ui-card-content { padding: 0 1.4rem 1rem; } .login08-root .ld8-card .ui-card-footer { padding: 0 1.4rem 1.5rem; } .login08-root .ld8-title { font-size: 1.5rem; } .login08-root .ld8-badge { width: 44px; height: 44px; border-radius: 13px; } }",

        /* Fields */
        ".login08-root .ld8-group { margin-bottom: .35rem; }",
        ".login08-root .ld8-label { color: var(--foreground); font-weight: 600; font-size: .84rem; margin-bottom: .5rem; }",
        ".login08-root .ld8-labelrow { display: flex; align-items: center; justify-content: space-between; gap: .75rem; margin-bottom: .5rem; }",
        ".login08-root .ld8-labelrow .ld8-label { margin-bottom: 0; }",
        ".login08-root .ld8-field { position: relative; }",
        ".login08-root .ld8-field .ui-input { height: 3.2rem; border-radius: 10px; background: #fff; color: #0f172a; padding: 0 4.4rem 0 2.9rem; font-size: .95rem; font-weight: 500; border: 1.5px solid #cbd5e1; }",
        ".login08-root .ld8-field .ui-input:hover { border-color: #60a5fa; }",
        ".login08-root .ld8-field .ui-input:focus-visible { background: #fff; border-color: #2563eb; box-shadow: 0 0 0 4px rgba(37,99,235,0.16); }",
        ".login08-root .ld8-field .ui-input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px #f8fafc inset; -webkit-text-fill-color: " + INK + "; caret-color: " + INK + "; }",
        ".login08-root .ld8-fieldicon { position: absolute; left: .95rem; top: 50%; margin-top: -11px; color: #94a3b8; pointer-events: none; transition: color .2s ease; }",
        ".login08-root .ld8-field:focus-within .ld8-fieldicon { color: #2563eb; }",
        ".login08-root .ld8-capsicon { position: absolute; right: .95rem; top: 50%; margin-top: -10px; pointer-events: none; }",
        ".login08-root .ld8-capsicon-pw { right: 3.3rem; }",
        ".login08-root .ld8-toggle { position: absolute; right: .2rem; top: 50%; margin-top: -1.375rem; height: 2.75rem; width: 2.75rem; color: #64748b; border-radius: 10px; }",
        ".login08-root .ld8-toggle:hover { color: #1d4ed8; background: rgba(37,99,235,0.08); }",
        ".login08-root .ld8-caps { display: flex; align-items: center; gap: .35rem; color: #b45309; font-size: 12px; padding-top: .35rem; }",
        ".login08-root .ld8-link { color: #1d4ed8; text-decoration: none; font-weight: 700; font-size: .83rem; border-radius: 4px; transition: color .2s ease; }",
        ".login08-root .ld8-link:hover { color: #1d4ed8; text-decoration: underline; text-underline-offset: 3px; }",
        ".login08-root .ld8-link:focus-visible { outline: 2px solid #2563eb; outline-offset: 3px; }",
        ".login08-root .ld8-alert { display: flex; align-items: center; gap: .6rem; background: #fff1f2; border-color: #fecdd3; color: #be123c; font-weight: 500; font-size: .83rem; margin-bottom: 1rem; border-radius: 12px; }",

        /* Buttons */
        ".login08-root .ld8-primary { width: 100%; height: 3.3rem; border-radius: 14px; font-size: .98rem; font-weight: 700; letter-spacing: .2px; color: #fff; background: " + BTN_GRADIENT + "; background-size: 150% 100%; background-position: 0% 0%; box-shadow: 0 12px 26px -8px rgba(37,99,235,0.6); transition: background-position .45s ease, box-shadow .25s ease, opacity .15s; }",
        ".login08-root .ld8-primary:hover { opacity: 1; background-position: 100% 0%; box-shadow: 0 16px 32px -8px rgba(29,78,216,0.65); }",
        ".login08-root .ld8-primary:disabled { opacity: .65; box-shadow: none; }",
        ".login08-root .ld8-primary svg { transition: transform .2s ease; }",
        ".login08-root .ld8-primary:hover svg { transform: translateX(4px); }",
        ".login08-root .ld8-bio { width: 100%; height: 3rem; margin-top: .75rem; border-radius: 14px; font-weight: 700; color: #1d4ed8; border-color: #bfdbfe; }",
        ".login08-root .ld8-bio:hover { background: #eff6ff; border-color: #2563eb; }",

        /* Company cards */
        ".login08-root .ld8-companylist { display: flex; flex-direction: column; gap: .65rem; max-height: 300px; overflow-y: auto; padding: 4px; margin: 0 -4px 1rem; }",
        ".login08-root .ld8-company { display: flex; align-items: center; gap: .9rem; padding: .85rem 1rem; border-radius: 16px; border: 1.5px solid var(--border); background: #f8fafc; cursor: pointer; outline: none; box-shadow: none; transition: border-color .2s ease, background-color .2s ease, box-shadow .2s ease; }",
        ".login08-root .ld8-company:hover { border-color: #93c5fd; }",
        ".login08-root .ld8-company:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ring); }",
        ".login08-root .ld8-company[aria-checked='true'] { border-color: #2563eb; background: #eff6ff; box-shadow: 0 0 0 4px rgba(37,99,235,0.12); }",
        ".login08-root .ld8-company[aria-checked='true']:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 5px var(--ring); }",
        ".login08-root .ld8-avatar { width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1rem; color: #1e40af; background: #dbeafe; }",
        ".login08-root .ld8-company[aria-checked='true'] .ld8-avatar { color: #fff; background: " + BTN_GRADIENT + "; }",
        ".login08-root .ld8-companyname { flex: 1; min-width: 0; font-weight: 600; font-size: .92rem; color: var(--foreground); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",
        ".login08-root .ld8-radio { width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 2px solid #bfdbfe; background: #fff; }",
        ".login08-root .ld8-company[aria-checked='true'] .ld8-radio { border-color: #2563eb; background: #2563eb; color: #fff; }",

        /* Hero badge */
        ".login08-root .ld8-eyebrow { background: #eff6ff; border: 1px solid #bfdbfe; color: #1d4ed8; padding: .4rem .9rem; gap: .55rem; font-size: .7rem; font-weight: 700; letter-spacing: 1.6px; text-transform: uppercase; max-width: 100%; text-align: center; }",
        ".login08-root .ld8-panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.12); }",
        "@media (max-width: 420px) { .login08-root .ld8-eyebrow { letter-spacing: 1.2px; font-size: .64rem; padding: .4rem .8rem; } }",

        /* Session dialog buttons */
        ".login08-root .ld8-signout { min-width: 82px; flex-shrink: 0; font-size: .75rem; font-weight: 700; border-radius: 10px; background: #e11d48; color: #fff; }",
        ".login08-root .ld8-cancel { width: 100%; font-weight: 600; color: " + MUTED + "; border-radius: 12px; }",
        ".login08-root .ld8-cancel:hover { background: #f8fafc; border-color: #bfdbfe; color: #1d4ed8; }",

        "@media (max-width: 767.95px) { .login08-root .ld8-field .ui-input { font-size: 16px; } .login08-root .ld8-link { display: inline-block; padding: .5rem 0; } }",
        "@media (max-height: 520px) { .login08-root .ld8-head { padding-top: 1rem; padding-bottom: .8rem; } .login08-root .ld8-companylist { max-height: 160px; } }",
        "@media (prefers-reduced-motion: reduce) { .login08-root, .login08-root *, .login08-root *::before, .login08-root *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; } }",
      ].join("\n")}</style>

      {/* ── DECORATIVE BACKGROUND ── */}
      <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0, contain: "strict" }}>
        {/* static dot grid + soft blue glow — no animation, no blur */}
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)", backgroundSize: "22px 22px", opacity: 0.55, maskImage: "linear-gradient(180deg, #000 0%, transparent 85%)", WebkitMaskImage: "linear-gradient(180deg, #000 0%, transparent 85%)" }} />
        <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 55% 45% at 12% 0%, rgba(37,99,235,0.10) 0%, transparent 70%), radial-gradient(ellipse 45% 40% at 100% 100%, rgba(14,165,233,0.10) 0%, transparent 70%)" }} />
      </Box>

      {/* ── CONTENT ── */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          width: "100%",
          maxWidth: 1560,
          mx: "auto",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: { xs: "center", md: "flex-start" },
          gap: { md: 4, lg: 8 },
          px: { xs: 2, sm: 4, md: 6, xl: 10 },
          pt: { xs: 3, sm: 5, md: 6 },
          pb: { xs: 2.5, sm: 3, md: 6 },
        }}
      >
        {/* HERO COLUMN */}
        <Box
          component="section"
          aria-label="About ReonBook"
          sx={{
            flex: { md: 1 },
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            justifyContent: { md: "space-between" },
            textAlign: { xs: "center", md: "left" },
            mb: { xs: 3.5, md: 0 },
          }}
        >
          {/* Logo */}
          <Box
            component={motion.div}
            initial={{ opacity: 1, y: reduce ? 0 : -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "center", md: "flex-start" }, gap: 1.4 }}
          >
            <Box component="img" src="/RBLOGOBLUE.svg" alt="ReonBook" sx={{ height: { xs: 40, md: 48 } }} />
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <Badge variant="outline" className="ld8-eyebrow">Enterprise Accounting Platform</Badge>
            </Box>
          </Box>

          {/* Headline (desktop) */}
          <Box
            component={motion.div}
            variants={groupVariants}
            initial="hidden"
            animate="show"
            sx={{ display: { xs: "none", md: "block" }, my: 6, maxWidth: 620 }}
          >
            <Box component={motion.div} variants={itemVariants} sx={{ mb: 3 }}>
              <Badge variant="outline" className="ld8-eyebrow">
                <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#22c55e", boxShadow: "0 0 0 3px rgba(34,197,94,0.2)" }} />
                Enterprise Accounting Platform
              </Badge>
            </Box>
            <Typography component={motion.h2} variants={itemVariants} sx={{ fontSize: { md: "2.1rem", lg: "2.6rem", xl: "3rem" }, fontWeight: 800, color: INK, lineHeight: 1.12, letterSpacing: "-1px", mb: 2 }}>
              Run your whole business
              <br />
              <Box component="span" sx={{ color: "#2563eb" }}>
                from one ERP
              </Box>
            </Typography>
            <Typography component={motion.p} variants={itemVariants} sx={{ color: MUTED, fontSize: { md: "0.95rem", lg: "1rem" }, lineHeight: 1.7, maxWidth: 500, mb: 3 }}>
              Invoices, GST compliance, cash flow and multi-company ledgers — together in one place.
            </Typography>

            <Box component="ul" sx={{ listStyle: "none", m: 0, mt: 2, p: 0, display: "flex", flexWrap: "wrap", gap: 1, maxWidth: 580 }}>
              {modules.map((m) => (
                <Box
                  component={motion.li}
                  key={m}
                  variants={itemVariants}
                  sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 1.4, py: 0.8, borderRadius: "999px", bgcolor: "#fff", border: "1px solid #e2e8f0" }}
                >
                  <CheckIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                  <Typography sx={{ color: INK, fontSize: "0.8rem", fontWeight: 600 }}>{m}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Footer (desktop) */}
          {renderFooter({ display: { xs: "none", md: "block" }, textAlign: "left" })}
        </Box>

        {/* CARD COLUMN */}
        <Box
          sx={{
            position: "relative",
            width: { xs: "100%", md: 400, lg: 440 },
            maxWidth: { xs: 460, md: "none" },
            flexShrink: 0,
            alignSelf: { xs: "center", md: "flex-end" },
            boxSizing: "border-box",
          }}
        >
          {/* The card */}
          <MotionCard
            className="ld8-card"
            initial={{ opacity: 1, y: reduce ? 0 : 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0.2 : 0.6, delay: reduce ? 0 : 0.05, ease: EASE }}
          >
            <AnimatePresence mode="wait">
              {!isCompanySelectionVisible ? (

                /* ── LOGIN FORM ── */
                <motion.div key="login" variants={groupVariants} initial="hidden" animate="show" exit="exit">
                  <CardHeader className="ld8-head">
                    <motion.div variants={itemVariants} className="ld8-badge" aria-hidden="true">
                      <LockOutlinedIcon sx={{ fontSize: 25 }} />
                    </motion.div>
                    <motion.div variants={itemVariants} className="ld8-headtext">
                      <CardTitle role="heading" aria-level={1} className="ld8-title">Welcome</CardTitle>
                      <CardDescription className="ld8-desc">Sign in to access your ReonBook dashboard</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                      <motion.div variants={fieldVariants} className="ld8-group">
                        <Label htmlFor="ld8-identifier" className="ld8-label" style={{ display: "block", marginBottom: "0.5rem" }}>
                          Username, Email or Mobile
                        </Label>
                        <div className="ld8-field">
                          <PersonOutlineIcon className="ld8-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld8-identifier"
                            type="text"
                            autoComplete="username"
                            placeholder="Username, email or mobile number"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld8-capsicon" sx={{ color: "#f59e0b", fontSize: 20 }} aria-hidden="true" />}
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div variants={fieldVariants} className="ld8-group" style={{ marginTop: "1rem" }}>
                        <div className="ld8-labelrow">
                          <Label htmlFor="ld8-password" className="ld8-label">Password</Label>
                          <Link to="/forgot-password" className="ld8-link">Forgot password?</Link>
                        </div>
                        <div className="ld8-field">
                          <LockOutlinedIcon className="ld8-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld8-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld8-capsicon ld8-capsicon-pw" sx={{ color: "#f59e0b", fontSize: 20 }} aria-hidden="true" />}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ld8-toggle"
                            onClick={handleTogglePasswordVisibility}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            aria-pressed={showPassword}
                          >
                            {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                          </Button>
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div variants={fieldVariants} style={{ marginTop: "1.25rem" }}>
                        {errorAlert}

                        <MotionButton
                          type="submit"
                          size="lg"
                          disabled={isLoading}
                          aria-busy={isLoading}
                          className="ld8-primary"
                          whileHover={reduce || isLoading ? undefined : { y: -2 }}
                          whileTap={reduce || isLoading ? undefined : { scale: 0.98 }}
                          transition={{ type: "spring", stiffness: 400, damping: 22 }}
                        >
                          {isLoading
                            ? <><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</>
                            : <>Sign In<ArrowForwardIcon sx={{ fontSize: 20 }} /></>}
                        </MotionButton>

                        {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                          <Button
                            type="button"
                            variant="outline"
                            className="ld8-bio"
                            onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                            Login with Biometric
                          </Button>
                        )}
                      </motion.div>
                    </form>

                    <motion.div variants={itemVariants}>
                      <Separator style={{ margin: "1.5rem 0 0" }} />
                    </motion.div>
                  </CardContent>

                  <CardFooter>
                    <motion.p variants={itemVariants} style={{ margin: 0 }}>
                      Don't have an account?{" "}
                      <Link to="/register" className="ld8-link" style={{ fontSize: "inherit" }}>
                        Sign up now
                      </Link>
                    </motion.p>
                  </CardFooter>
                </motion.div>

              ) : (

                /* ── COMPANY SELECTION ── */
                <motion.div key="company" variants={groupVariants} initial="hidden" animate="show" exit="exit">
                  <CardHeader className="ld8-head">
                    <motion.div variants={itemVariants} className="ld8-badge" aria-hidden="true">
                      <BusinessIcon sx={{ fontSize: 25 }} />
                    </motion.div>
                    <motion.div variants={itemVariants} className="ld8-headtext">
                      <CardTitle role="heading" aria-level={1} className="ld8-title" style={{ fontSize: "1.55rem" }}>Select Organization</CardTitle>
                      <CardDescription className="ld8-desc" id="ld8-company-desc">Choose which organization to access</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <motion.div
                      variants={itemVariants}
                      className="ld8-companylist"
                      role="radiogroup"
                      aria-label="Choose your organization"
                      aria-describedby="ld8-company-desc"
                    >
                      {companies.map((c, idx) => {
                        const isSel = selectedCompany?.companyId === c.companyId;
                        const firstFocusable = !selectedCompany && idx === 0;
                        return (
                          <MotionCard
                            key={c.companyId}
                            id={"ld8-company-" + idx}
                            role="radio"
                            aria-checked={isSel}
                            tabIndex={isSel || firstFocusable ? 0 : -1}
                            className="ld8-company"
                            onClick={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                            onKeyDown={(e) => handleCompanyKeyDown(e, idx)}
                            whileHover={reduce ? undefined : { y: -2 }}
                            whileTap={reduce ? undefined : { scale: 0.99 }}
                            transition={{ type: "spring", stiffness: 380, damping: 24 }}
                          >
                            <span className="ld8-avatar" aria-hidden="true">{(c.companyName || "?").trim().charAt(0).toUpperCase()}</span>
                            <span className="ld8-companyname">{c.companyName}</span>
                            <span className="ld8-radio" aria-hidden="true">
                              <AnimatePresence initial={false}>
                                {isSel && (
                                  <motion.span
                                    key="tick"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    transition={{ duration: reduce ? 0.05 : 0.18 }}
                                    style={{ display: "flex" }}
                                  >
                                    <CheckIcon sx={{ fontSize: 14 }} />
                                  </motion.span>
                                )}
                              </AnimatePresence>
                            </span>
                          </MotionCard>
                        );
                      })}
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      {errorAlert}

                      <MotionButton
                        type="button"
                        size="lg"
                        className="ld8-primary"
                        onClick={() => FN_SelectCompany()}
                        whileHover={reduce ? undefined : { y: -2 }}
                        whileTap={reduce ? undefined : { scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 400, damping: 22 }}
                      >
                        Continue to Dashboard<ArrowForwardIcon sx={{ fontSize: 20 }} />
                      </MotionButton>
                    </motion.div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </MotionCard>
        </Box>

        {/* Footer (mobile / tablet portrait) */}
        {renderFooter({ display: { xs: "block", md: "none" }, mt: 3.5 })}
      </Box>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="login08-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="ld8-session-title"
        slots={{ transition: Grow }}
        transitionDuration={{ enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { backdropFilter: "blur(5px)", bgcolor: "rgba(15,23,42,0.45)" } },
          paper: { sx: { borderRadius: "24px", overflow: "hidden", m: 2, width: "calc(100% - 32px)" } },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="login08-root">
            {/* Top banner */}
            <Box sx={{ position: "relative", overflow: "hidden", background: BANNER_GRADIENT, px: 3, pt: 3, pb: 2.5 }}>
              <Box aria-hidden="true" sx={{ position: "absolute", width: 170, height: 170, borderRadius: "50%", top: -70, right: -40, background: "linear-gradient(135deg, rgba(255,255,255,0.28), rgba(255,255,255,0.02))" }} />
              <Box aria-hidden="true" sx={{ position: "absolute", width: 90, height: 90, borderRadius: "50%", bottom: -36, left: "42%", background: "linear-gradient(135deg, rgba(244,114,182,0.5), rgba(167,139,250,0.05))" }} />
              <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: "13px", bgcolor: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DevicesIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography id="ld8-session-title" component="h2" sx={{ fontWeight: 800, fontSize: "1.08rem", color: "#fff", lineHeight: 1.3 }}>
                    Session Limit Reached
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.86)", mt: 0.2 }}>
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ position: "relative", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "12px", px: 2, py: 1 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#fff", lineHeight: 1.5 }}>
                  Sign out of one of the devices below to log in on this device.
                </Typography>
              </Box>
            </Box>

            {/* Device list */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", mb: 1.5 }}>
                Active Sessions
              </Typography>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <Box key={session.id}
                  component={motion.div}
                  initial={{ opacity: 1, y: reduce ? 0 : 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: reduce ? 0 : 0.1 + idx * 0.07, ease: EASE }}
                  whileHover={reduce ? undefined : { y: -1 }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: 1.5, mb: 1, borderRadius: "14px",
                    border: "1px solid #e2e8f0",
                    bgcolor: "#f8fafc",
                    transition: "border-color .2s ease, box-shadow .2s ease",
                    "&:hover": { borderColor: "#60a5fa", boxShadow: "0 8px 18px -8px rgba(37,99,235,0.35)" },
                  }}>
                  {/* Icon */}
                  <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {deviceIcon(session.device_type)}
                  </Box>

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.87rem", color: INK }}>
                      {session.device_name || "Unknown Device"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                      <AccessTimeIcon sx={{ fontSize: 12, color: MUTED }} />
                      <Typography sx={{ fontSize: "0.73rem", color: MUTED }}>
                        {timeAgo(session.last_activity)}
                      </Typography>
                      {session.ip_address && (
                        <Typography sx={{ fontSize: "0.73rem", color: "#94a3b8", ml: 0.5 }}>
                          · {session.ip_address}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Action */}
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="ld8-signout"
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </Button>
                </Box>
              ))}
            </Box>

            {/* Cancel */}
            <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5, textAlign: "center" }}>
              <Button
                type="button"
                variant="outline"
                className="ld8-cancel"
                onClick={() => setSessionPickerData(null)}
              >
                Cancel
              </Button>
            </Box>
          </ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign08;
